import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import { useLanguage } from '@ui/i18n';
import { ArrowBack, ArrowForward } from '@veno-app/icons';
import { format, setDay, setMonth } from 'date-fns';
import type { DateObj, RenderProps } from 'dayzed';
import type { FC } from 'react';
import { useMemo } from 'react';

export type SelectionStatus =
  | 'none'
  | 'start'
  | 'mid'
  | 'end'
  | 'single'
  | 'disabled'
  | 'hover';

export type CalendarBodyProps = BoxProps &
  RenderProps & {
    onSelectionStatus: (dateObj: DateObj) => SelectionStatus;
    onMouseMoveOnCell?: (dateObj: DateObj) => void;
  };
export const CalendarBody: FC<CalendarBodyProps> = ({
  calendars,
  getBackProps,
  getForwardProps,
  getDateProps,
  onSelectionStatus,
  onMouseMoveOnCell,
  ...props
}) => {
  const weekdayNamesShort = useWeekdayNamesShort();
  const monthNames = useMonthNames();

  if (!calendars.length) {
    return null;
  }
  return (
    <Box {...props}>
      {calendars.map((calendar) => {
        return (
          <Box key={`${calendar.month}${calendar.year}`} maxW="340px" mx="auto">
            <Flex justifyContent="space-between" alignItems="center">
              <CalendarCell {...getBackProps({ calendars })}>
                <Icon as={ArrowBack} />
              </CalendarCell>
              <Text color="text-light" textStyle="h4">
                {monthNames[calendar.month]} {calendar.year}
              </Text>
              <CalendarCell {...getForwardProps({ calendars })}>
                <Icon as={ArrowForward} />
              </CalendarCell>
            </Flex>
            <Flex justifyContent="center">
              {weekdayNamesShort.map((weekday) => {
                return (
                  <CalendarCell
                    key={`${calendar.month}${calendar.year}${weekday}`}
                    color="text-light"
                    cursor="default"
                  >
                    {weekday}
                  </CalendarCell>
                );
              })}
            </Flex>
            {calendar.weeks.map((week, weekIndex) => {
              return (
                <Flex key={weekIndex} justifyContent="center">
                  {week.map((dateObj, index) => {
                    const key = `${calendar.month}${calendar.year}${weekIndex}${index}`;
                    if (!dateObj) {
                      return <CalendarCell key={key} cursor="default" />;
                    }

                    return (
                      <CalendarCell
                        key={key}
                        selectionStatus={
                          dateObj.selectable
                            ? onSelectionStatus(dateObj)
                            : 'disabled'
                        }
                        onMouseMove={() => {
                          onMouseMoveOnCell?.(dateObj);
                        }}
                        {...getDateProps({ dateObj })}
                      >
                        {dateObj.date.getDate()}
                      </CalendarCell>
                    );
                  })}
                </Flex>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

type CalendarCellProps = BoxProps & {
  selectionStatus?: SelectionStatus;
};
const CalendarCell: FC<CalendarCellProps> = ({
  selectionStatus,
  children,
  ...props
}) => {
  const selectionStyle = useMemo<BoxProps>(() => {
    if (selectionStatus === 'start') {
      return {
        bg: '#FFFFFFBF',
        borderRadius: '4px 0px 0px 4px',
        borderRight: 'none',
      };
    }
    if (selectionStatus === 'mid') {
      return {
        bg: '#FFFFFFBF',
        borderLeft: 'none',
        borderRight: 'none',
      };
    }
    if (selectionStatus === 'end') {
      return {
        bg: '#FFFFFFBF',
        borderRadius: '0px 4px 4px 0px',
        borderLeft: 'none',
      };
    }
    if (selectionStatus === 'single') {
      return {
        bg: '#FFFFFFBF',
        borderRadius: '4px',
      };
    }
    if (selectionStatus === 'disabled') {
      return {
        color: 'text-light',
        cursor: 'not-allowed',
        border: 'none',
        opacity: 0.4,
      };
    }
    if (selectionStatus === 'hover') {
      return {
        border: 'none',
        bg: 'blackAlpha.100',
        p: '0.5px',
        bgClip: 'content-box',
      };
    }
    return {
      border: 'none',
    };
  }, [selectionStatus]);

  const currentCursor: BoxProps['cursor'] =
    selectionStyle.cursor || props.cursor || 'pointer';

  return (
    <Flex w="49px" h="49px" position="relative">
      <Flex
        w="calc(100% + 1px)"
        h="calc(100% + 1px)"
        position="absolute"
        left="-0.5px"
        top="-0.5px"
        justifyContent="center"
        alignItems="center"
        borderWidth="1px"
        borderColor="brand-primary"
        color="brand-primary"
        textStyle="body"
        cursor={currentCursor}
        _hover={{
          bg: currentCursor === 'pointer' ? 'blackAlpha.100' : undefined,
        }}
        {...selectionStyle}
        {...props}
      >
        {children}
      </Flex>
    </Flex>
  );
};

const useWeekdayNamesShort = () => {
  const language = useLanguage();

  return useMemo(() => {
    const names: string[] = [];

    for (let i = 0; i < 7; i++) {
      names.push(
        format(setDay(Date.now(), i), 'eee', {
          locale: language.dateFnsLocale,
        }),
      );
    }

    return names;
  }, [language]);
};

const useMonthNames = () => {
  const language = useLanguage();

  return useMemo(() => {
    const names: string[] = [];

    for (let i = 0; i < 12; i++) {
      names.push(
        format(setMonth(Date.now(), i), 'MMMM', {
          locale: language.dateFnsLocale,
        }),
      );
    }

    return names;
  }, [language]);
};
